import React, { useMemo, useState } from 'react';
import { useT } from '../i18n/LocaleContext';
import { useData } from '../data/DataContext';
import { useToast } from '../contexts/ToastContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import DataTable from '../components/ui/DataTable';
import EmptyState from '../components/ui/EmptyState';
import SearchRow, { SearchInput } from '../components/ui/SearchRow';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import UserFormModal from '../components/users/UserFormModal';
import { formatDate, formatRelativeTime } from '../utils/format';
import useDebounce from '../hooks/useDebounce';

const ROLE_TONE = { admin: 'admin', tresorier: 'tresorier', coordinateur: 'coordinateur', secretaire: 'pelerinage' };

export default function UsersPage() {
  const t = useT();
  const toast = useToast();
  const { users, createUser, updateUser, setUserActive } = useData();

  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 200);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const filtered = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    return users.filter((u) => {
      if (!q) return true;
      return (
        u.username.toLowerCase().includes(q) ||
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
        (u.phone || '').toLowerCase().includes(q)
      );
    });
  }, [users, debouncedQuery]);

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (u) => { setEditing(u); setFormOpen(true); };

  const handleSubmit = async (data) => {
    if (editing) {
      updateUser(editing.id, data);
      toast.success(t('users.toast_updated', { name: `${data.firstName} ${data.lastName}` }));
    } else {
      const next = createUser(data);
      toast.success(t('users.toast_created', { name: `${next.firstName} ${next.lastName}` }));
    }
    setFormOpen(false);
  };

  const handleConfirmToggle = async () => {
    if (!confirm) return;
    const { user, action } = confirm;
    setUserActive(user.id, action === 'activate');
    toast.success(
      action === 'activate'
        ? t('users.toast_activated', { name: `${user.firstName} ${user.lastName}` })
        : t('users.toast_deactivated', { name: `${user.firstName} ${user.lastName}` })
    );
    setConfirm(null);
  };

  const columns = [
    {
      key: 'name',
      header: t('common.full_name'),
      render: (u) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <strong>{u.firstName} {u.lastName}</strong>
          {!u.active && <Badge tone="inactive">{t('members.statuses.inactive')}</Badge>}
        </span>
      ),
    },
    { key: 'username', header: t('users.field_username'), cellClassName: 'mono', render: (u) => u.username },
    { key: 'role', header: t('users.field_role'), render: (u) => <Badge tone={ROLE_TONE[u.role]}>{t(`users.roles.${u.role}`)}</Badge> },
    { key: 'phone', header: t('users.field_phone'), render: (u) => u.phone },
    {
      key: 'lastLogin',
      header: t('users.field_last_login'),
      render: (u) => u.lastLoginAt ? formatRelativeTime(u.lastLoginAt, t) : formatDate(u.lastLoginAt, t) || '—',
    },
    {
      key: 'actions',
      header: t('common.actions'),
      render: (u) => (
        <div className="action-row">
          <button type="button" className="action-btn" onClick={() => openEdit(u)}>{t('common.edit')}</button>
          {u.active ? (
            <button type="button" className="action-btn danger" onClick={() => setConfirm({ user: u, action: 'deactivate' })}>
              {t('common.deactivate')}
            </button>
          ) : (
            <button type="button" className="action-btn" onClick={() => setConfirm({ user: u, action: 'activate' })}>
              {t('common.activate')}
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <SearchRow>
        <SearchInput value={query} onChange={setQuery} placeholder={t('users.search_placeholder')} />
        <Button variant="gold" onClick={openNew}>+ {t('users.new_user')}</Button>
      </SearchRow>

      <Card title={t('users.title')} subtitle={t('users.subtitle')} padded={false}>
        <DataTable
          columns={columns}
          rows={filtered}
          empty={<EmptyState title={t('common.no_data')} />}
        />
      </Card>

      <UserFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
        initial={editing}
      />

      <ConfirmDialog
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={handleConfirmToggle}
        title={confirm?.action === 'activate' ? t('users.activate_confirm_title') : t('users.deactivate_confirm_title')}
        body={
          confirm
            ? (confirm.action === 'activate'
              ? t('users.activate_confirm_body', { name: `${confirm.user.firstName} ${confirm.user.lastName}` })
              : t('users.deactivate_confirm_body', { name: `${confirm.user.firstName} ${confirm.user.lastName}` }))
            : ''
        }
        confirmLabel={confirm?.action === 'activate' ? t('common.activate') : t('common.deactivate')}
        variant={confirm?.action === 'activate' ? 'primary' : 'danger'}
      />
    </div>
  );
}
